import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth.jsx';
import { useLang } from '../i18n.jsx';
import { useCierreSuave, useFocoModal, useVolverCierra } from '../movimiento.js';
import LangSwitch from './LangSwitch.jsx';

/**
 * Entrar, crear cuenta, recuperar y ver la propia cuenta, todo en un panel.
 *
 * SIN RUTAS, A PROPOSITO
 *
 * La aplicacion no tiene enrutador -- las pestañas son estado -- y meter uno
 * sólo para tres formularios sería desproporcionado. Ademas esto termina siendo
 * app de teléfono, donde un panel que sube desde abajo es el gesto natural y una
 * pagina aparte se siente como salir de la aplicacion.
 *
 * MOVIL PRIMERO
 *
 * El panel ocupa la pantalla en teléfono y se centra en escritorio. Los campos
 * llevan `autocomplete` real para que el gestor de contraseñas los reconozca:
 * sin eso, la gente acaba escribiendo contraseñas a mano y eligiendo peores.
 */

const MODOS = { entrar: 'signIn', registro: 'signUp', recuperar: 'recoverTitle', reset: 'resetTitle' };

/**
 * Campo de formulario.
 *
 * Va FUERA del componente a propósito. Definido dentro, React lo ve como un tipo
 * de componente distinto en cada render y desmonta el <input> para volver a
 * crearlo: el efecto visible es que el foco salta fuera del campo a cada tecla y
 * escribir se vuelve imposible. Es un fallo silencioso — el código parece
 * correcto — que sólo se nota al usarlo.
 *
 * `autoComplete` no es adorno: sin él el gestor de contraseñas no reconoce el
 * formulario, y quien no puede autocompletar termina eligiendo contraseñas
 * peores porque las escribe a mano.
 */
function Campo({ id, label, tipo = 'text', valor, onChange, auto, hint, campoRef, required }) {
  const { t } = useLang();
  /*
   * Ojito para DESTAPAR la contraseña. El campo enmascarado protege de
   * miradas ajenas, pero castiga el caso comun: una persona sola que quiere
   * comprobar lo que escribio. El estado vive en cada campo -- se destapa el
   * que se pide, no todos -- y siempre nace tapado.
   */
  const esPassword = tipo === 'password';
  const [visible, setVisible] = useState(false);

  const input = (
    <input
      id={id}
      ref={campoRef}
      type={esPassword && visible ? 'text' : tipo}
      value={valor}
      onChange={onChange}
      autoComplete={auto}
      required={required}
      autoCapitalize="off"
      spellCheck="false"
    />
  );

  return (
    <label className="acc__field" htmlFor={id}>
      <span className="acc__label">
        {label}
        {hint && <span className="acc__hint"> · {hint}</span>}
      </span>
      {esPassword ? (
        <span className="acc__passwrap">
          {input}
          <button
            type="button"
            className="acc__ojito"
            onClick={() => setVisible((v) => !v)}
            aria-label={t(visible ? 'hidePassword' : 'showPassword')}
            title={t(visible ? 'hidePassword' : 'showPassword')}
            /* Fuera del orden de tabulacion: correo→contraseña→Entrar sin
               parada extra. Con raton o dedo sigue a un toque. */
            tabIndex={-1}
          >
            {visible ? (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
                <circle cx="12" cy="12" r="2.6" />
                <line x1="4" y1="3" x2="20" y2="21" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" />
                <circle cx="12" cy="12" r="2.6" />
              </svg>
            )}
          </button>
        </span>
      ) : (
        input
      )}
    </label>
  );
}

export default function AccountPanel({ open, onClose, modoInicial = 'entrar', resetToken = null }) {
  const { t } = useLang();
  const { user, entrar, registrar, recuperar, restablecer, salir, reenviarVerificacion } = useAuth();
  const [modo, setModo] = useState(modoInicial);
  const [form, setForm] = useState({ email: '', password: '', name: '', invite: '' });
  const [error, setError] = useState(null);
  const [aviso, setAviso] = useState(null);
  const { ref: refPanel, cerrando, pedirCierre } = useCierreSuave(onClose, open);
  useFocoModal(refPanel, open);
  useVolverCierra(open, pedirCierre);
  const [busy, setBusy] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const primerCampo = useRef(null);

  useEffect(() => {
    if (open) {
      setModo(modoInicial);
      setError(null);
      setAviso(null);
    }
  }, [open, modoInicial]);

  // El foco entra al panel al abrirse: en teclado y en lector de pantalla, si no
  // se mueve el foco el panel "no existe" aunque se vea.
  useEffect(() => {
    if (open) primerCampo.current?.focus();
  }, [open, modo]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && pedirCierre();
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, pedirCierre]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const enviar = async (e) => {
    e.preventDefault();
    setError(null);
    setAviso(null);
    setBusy(true);
    try {
      let r;
      if (modo === 'entrar') r = await entrar(form.email, form.password);
      else if (modo === 'registro') {
        r = await registrar({
          email: form.email,
          password: form.password,
          name: form.name || undefined,
          invite: form.invite || undefined,
        });
      } else if (modo === 'recuperar') {
        r = await recuperar(form.email);
        // Se avisa igual exista o no la cuenta: decir "ese correo no está" haría
        // de este formulario un comprobador de quién tiene cuenta.
        if (r.ok) setAviso(t('recoverSent'));
      } else if (modo === 'reset') {
        r = await restablecer(resetToken, form.password);
      }
      if (!r.ok) setError(t(r.error));
      else if (modo === 'entrar' || modo === 'registro' || modo === 'reset') {
        /* La contraseña NO se queda en el estado: el panel no se desmonta, y
           al cerrar sesion el formulario reaparecia con ella puesta -- en un
           equipo compartido, el siguiente entraba con un clic (o la leia con
           el ojito). */
        setForm((f) => ({ ...f, password: '' }));
        pedirCierre();
      }
    } finally {
      setBusy(false);
    }
  };

  const copiarCodigo = async () => {
    try {
      await navigator.clipboard.writeText(user.referralCode);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      /* sin permiso de portapapeles: el código se ve igual y se puede seleccionar */
    }
  };

  const cuerpo = user ? (
    // --- Sesión iniciada ---
    <div className="acc__me">
      <div className="acc__identity">
        <span className="acc__avatar" aria-hidden="true">
          {(user.name || user.email)[0].toUpperCase()}
        </span>
        <div className="acc__who">
          <strong>{user.name || user.email}</strong>
          {user.name && <span className="muted">{user.email}</span>}
        </div>
      </div>

      {!user.emailVerified && (
        <div className="acc__notice">
          <span>{t('verifyPending')}</span>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await reenviarVerificacion();
              setAviso(t('verifySent'));
              setBusy(false);
            }}
          >
            {t('verifyResend')}
          </button>
        </div>
      )}

      {/*
        LA PLACA DE MEMBRESIA. Era una pareja `dt`/`dd` sueltas -- "PLAN / Pro"
        -- del mismo tamaño y peso que cualquier otro dato de la ficha, asi que
        lo unico que distinguia a un Pro de un gratis era una palabra en dorado.

        Ahora es un objeto: su placa, con el filo del modulo arriba como el
        resto de superficies de la casa, y en Pro ademas teñida de acento. Sigue
        siendo la misma informacion; lo que cambia es que se ve antes de leerla.

        El color NO es la unica señal, que es regla de la casa: la palabra
        "Pro" o "Gratis" sigue escrita y es la que manda. La placa solo la
        destaca.
      */}
      <div className={`placa${user.plan === 'pro' ? ' placa--pro' : ''}`}>
        <span className="placa__rotulo">{t('planLabel')}</span>
        <strong className="placa__plan">
          {user.plan === 'pro' ? t('planPro') : t('planFree')}
        </strong>
        {/* Los dias de regalo son un ESTADO del plan, no un dato aparte: viven
            en la placa porque explican por que alguien es Pro sin pagar. */}
        {user.bonusDays > 0 && (
          <span className="placa__bonus">{t('bonusDays', { n: user.bonusDays })}</span>
        )}
      </div>

      {user.referralCode && (
        <div className="acc__invite">
          <span className="acc__label">{t('myInviteCode')}</span>
          <div className="acc__inviterow">
            <code>{user.referralCode}</code>
            <button type="button" className="btn btn--ghost" onClick={copiarCodigo}>
              {copiado ? t('copied') : t('copy')}
            </button>
          </div>
          <p className="muted acc__explain">{t('inviteExplain')}</p>
        </div>
      )}

      {aviso && <p className="acc__ok" role="status">{aviso}</p>}

      {/* El idioma es un ajuste de la cuenta, no una herramienta de cabecera:
          se elige una vez. Tambien vive en el pie, para quien no ha entrado. */}
      <div className="acc__lang">
        <span className="acc__label">{t('language')}</span>
        <LangSwitch />
      </div>

      <button
        type="button"
        className="btn btn--ghost acc__wide"
        onClick={async () => {
          await salir();
          // Tras salir no queda rastro de quien estaba: ni correo ni clave.
          setForm({ email: '', password: '', name: '', invite: '' });
          pedirCierre();
        }}
      >
        {t('signOut')}
      </button>
    </div>
  ) : (
    // --- Sin sesión ---
    <form className="acc__form" onSubmit={enviar}>
      {modo === 'registro' && <p className="acc__pitch">{t('guestPitch')}</p>}
      {modo === 'recuperar' && <p className="acc__pitch">{t('recoverHint')}</p>}

      {modo !== 'reset' && (
        <Campo
          id="acc-email"
          label={t('email')}
          tipo="email"
          valor={form.email}
          onChange={set('email')}
          auto="email"
          campoRef={primerCampo}
          required
        />
      )}

      {modo !== 'recuperar' && (
        <Campo
          id="acc-password"
          label={modo === 'reset' ? t('newPassword') : t('password')}
          tipo="password"
          valor={form.password}
          onChange={set('password')}
          auto={modo === 'entrar' ? 'current-password' : 'new-password'}
          hint={modo === 'entrar' ? null : t('passwordMin')}
          campoRef={modo === 'reset' ? primerCampo : undefined}
          required
        />
      )}

      {modo === 'registro' && (
        <>
          <Campo
            id="acc-name"
            label={t('yourName')}
            valor={form.name}
            onChange={set('name')}
            auto="name"
            hint={t('optional')}
          />
          <Campo
            id="acc-invite"
            label={t('inviteCode')}
            valor={form.invite}
            onChange={(e) => setForm((f) => ({ ...f, invite: e.target.value.toUpperCase() }))}
            hint={t('optional')}
          />
        </>
      )}

      {error && <p className="acc__error" role="alert">{error}</p>}
      {aviso && <p className="acc__ok" role="status">{aviso}</p>}

      {/* La aceptación va DICHA junto al botón que la ejecuta, con la política
          a un clic. El modal lo abre App por evento: este formulario no tiene
          por qué conocerlo. */}
      {modo === 'registro' && (
        <p className="muted acc__legal">
          {t('signUpAccept').split('{link}')[0]}
          <button
            type="button"
            className="acc__legalLink"
            onClick={() => window.dispatchEvent(new Event('ptcg:abrir-privacidad'))}
          >
            {t('signUpAcceptLink')}
          </button>
          {t('signUpAccept').split('{link}')[1]}
        </p>
      )}

      <button type="submit" className="btn acc__wide" disabled={busy}>
        {busy ? t('sending') : t(MODOS[modo])}
      </button>

      <div className="acc__switch">
        {modo === 'entrar' && (
          <>
            <button type="button" onClick={() => { setModo('registro'); setError(null); }}>
              {t('signUp')}
            </button>
            <button type="button" onClick={() => { setModo('recuperar'); setError(null); }}>
              {t('forgotPassword')}
            </button>
          </>
        )}
        {(modo === 'registro' || modo === 'recuperar') && (
          <button type="button" onClick={() => { setModo('entrar'); setError(null); }}>
            {t('backToSignIn')}
          </button>
        )}
      </div>

      {modo !== 'reset' && <p className="muted acc__browsing">{t('guestBrowsing')}</p>}
    </form>
  );

  /*
   * Pinchar FUERA solo cierra cuando el panel es "Mi cuenta". En los modos con
   * formulario (entrar, registro, recuperar) un clic perdido -- apuntar mal al
   * campo, soltar una seleccion arrastrada de la contraseña -- tiraba lo
   * escrito. Ahi se cierra solo a proposito: la X, Escape o "atras".
   */
  return createPortal(
    <div
      className={`overlay overlay--acc${cerrando ? ' is-closing' : ''}`}
      onClick={user ? pedirCierre : undefined}
    >
      <div
        ref={refPanel}
        className="acc"
        role="dialog"
        aria-modal="true"
        aria-label={user ? t('myAccount') : t(MODOS[modo])}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="acc__head">
          <h2>{user ? t('myAccount') : t(MODOS[modo])}</h2>
          <button type="button" className="detail__close" onClick={pedirCierre} aria-label={t('close')}>
            ×
          </button>
        </header>
        {cuerpo}
      </div>
    </div>,
    document.body
  );
}
